/**
 * Auto Push Subscription Hook
 * Automatically subscribes authenticated users to push notifications
 * if they haven't explicitly disabled them and browser permission is granted or default
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Key to track if we've already attempted auto-subscription for this session
const AUTO_SUB_KEY = 'colectarium_auto_push_attempted';

export const useAutoPushSubscription = () => {
    const { isAuthenticated, token } = useAuth();
    const attemptedRef = useRef(false);

    useEffect(() => {
        // Only run once per session and when authenticated
        if (!isAuthenticated || !token || attemptedRef.current) return;
        
        // Check if we've already attempted this session
        const alreadyAttempted = sessionStorage.getItem(AUTO_SUB_KEY);
        if (alreadyAttempted) return;

        const autoSubscribe = async () => {
            attemptedRef.current = true;
            sessionStorage.setItem(AUTO_SUB_KEY, 'true');

            try {
                // Check browser support
                if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
                    console.log('[AutoPush] Browser does not support push notifications');
                    return;
                }

                // Check if permission is denied - don't bother trying
                if (Notification.permission === 'denied') {
                    console.log('[AutoPush] Notifications are blocked by user');
                    return;
                }

                // Check if user already has a subscription on backend
                const statusResponse = await fetch(`${API_URL}/api/notifications/subscription-status`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (statusResponse.ok) {
                    const statusData = await statusResponse.json();
                    if (statusData.has_subscription) {
                        console.log('[AutoPush] User already has push subscription');
                        return;
                    }
                }

                // Check if service worker is ready and has an existing subscription
                const registration = await navigator.serviceWorker.ready;
                let subscription = await registration.pushManager.getSubscription();

                if (subscription) {
                    // Local subscription exists, sync to backend
                    console.log('[AutoPush] Syncing existing local subscription to backend');
                    await syncSubscriptionToBackend(subscription, token);
                    return;
                }

                // If permission is default, request it
                if (Notification.permission === 'default') {
                    console.log('[AutoPush] Requesting notification permission...');
                    const permission = await Notification.requestPermission();
                    
                    if (permission !== 'granted') {
                        console.log('[AutoPush] User denied notification permission');
                        return;
                    }
                }

                // Permission granted, create subscription
                console.log('[AutoPush] Creating new push subscription...');
                
                // Get VAPID key
                const vapidResponse = await fetch(`${API_URL}/api/notifications/vapid-public-key`);
                if (!vapidResponse.ok) {
                    console.error('[AutoPush] Failed to get VAPID key');
                    return;
                }
                
                const { publicKey } = await vapidResponse.json();
                if (!publicKey) {
                    console.error('[AutoPush] VAPID key not configured');
                    return;
                }

                // Subscribe
                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey)
                });

                // Save to backend
                await syncSubscriptionToBackend(subscription, token);
                console.log('[AutoPush] Successfully subscribed to push notifications');

            } catch (error) {
                console.error('[AutoPush] Error during auto-subscription:', error);
            }
        };

        // Delay auto-subscription to not interfere with initial page load
        const timeoutId = setTimeout(autoSubscribe, 2000);
        
        return () => clearTimeout(timeoutId);
    }, [isAuthenticated, token]);
};

// Helper to sync subscription to backend
async function syncSubscriptionToBackend(subscription, token) {
    const p256dhKey = subscription.getKey('p256dh');
    const authKey = subscription.getKey('auth');

    if (!p256dhKey || !authKey) {
        throw new Error('Invalid subscription keys');
    }

    const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {
                p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(p256dhKey))),
                auth: btoa(String.fromCharCode.apply(null, new Uint8Array(authKey)))
            }
        })
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save subscription');
    }

    return response.json();
}

// Helper to convert VAPID key
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

export default useAutoPushSubscription;
